import { useCallback, useMemo } from "react";
import { getDocumentGroupByPage } from "../../utils/codeMetadataUtils";
import { getDocumentTypeLabel } from "../../utils/documentTypeUtils";

import type { DocumentGroup } from "services/document-group-api";

type ReferenceContextArgs = {
  pageNumber: string;
  documentGroupId?: number | null;
  facility?: string | null;
  provider?: string | null;
};

type UseDocumentGroupLookupArgs = {
  documentGroups: DocumentGroup[];
};

type UseDocumentGroupLookupReturn = {
  documentGroupOptions: { label: string; value: number }[];
  getReferenceContext: (args: ReferenceContextArgs) => {
    resolvedDocumentGroupId: number | null;
    resolvedFacility: string;
    provider: string;
  };
  getReferenceContextLabels: (args: ReferenceContextArgs) => {
    provider: string;
  };
};

/*
memoizes document group lookup function to prevent recreation on every render
provides reference context labels for code date details
*/
export const useDocumentGroupLookup = ({
  documentGroups,
}: UseDocumentGroupLookupArgs): UseDocumentGroupLookupReturn => {
  const documentGroupsById = useMemo(
    () => new Map(documentGroups.map((group) => [group.id, group])),
    [documentGroups],
  );

  const documentGroupOptions = useMemo(
    () =>
      documentGroups.map((group) => ({
        value: group.id,
        label: `${getDocumentTypeLabel(group.document_type)} (pages ${group.start_pg}-${group.end_pg})`,
      })),
    [documentGroups],
  );

  const getReferenceContext = useCallback(
    ({
      pageNumber,
      documentGroupId,
      facility,
      provider,
    }: ReferenceContextArgs) => {
      const documentGroup =
        (typeof documentGroupId === "number"
          ? documentGroupsById.get(documentGroupId)
          : undefined) ?? getDocumentGroupByPage(documentGroups, pageNumber);
      const resolvedDocumentGroupId = documentGroup?.id ?? null;
      const resolvedFacility = facility?.trim()
        ? facility
        : documentGroup?.facility?.trim()
          ? documentGroup.facility
          : "";
      const resolvedProvider = provider?.trim() ? provider : "";

      return {
        resolvedDocumentGroupId,
        resolvedFacility,
        provider: resolvedProvider,
      };
    },
    [documentGroups, documentGroupsById],
  );

  const getReferenceContextLabels = useCallback(
    (args: ReferenceContextArgs) => {
      const { provider } = getReferenceContext(args);
      return { provider };
    },
    [getReferenceContext],
  );

  return {
    documentGroupOptions,
    getReferenceContext,
    getReferenceContextLabels,
  };
};
